import { fetchSubProductsOrNominals } from '@api/useQueryCalls';
import BPNoDataFound from '@atoms/BPNoDataFound';
import { View } from '@components/primitives';
import useBPMutation from '@customHooks/useBPRequest';
import useFavourites from '@customHooks/useFavourites';
import navigationConstants from '@navigation/navigationConstants';
import { useToast } from '@hooks';
import RequestTransactionSheet from '@molecules/RequestTransactionSheet';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import { showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logErrorOnAnalytics, logPageBack, logPageLaunch } from './analyticsNominals';
import showListOfNominals from './nominalUtils';
import { refetchBPIfStale, shouldShowAccountInput, shouldShowPhoneSelection, shouldShowTitle } from '../BPUtils';

type GGNominalsProps = {
  route: {
    params: {
      source: string;
      productConfig: any;
    };
    productApiData: {
      prefix: string;
    };
  };
};

function GGNominals(props: GGNominalsProps) {
  const { bpPhone, setBPPhone, setInitialData, selectedBPAccount, validatedReferralCode, meAmplitudeContext } =
    getStore();
  const { mobileNumber } = bpPhone;
  const { accountTypeId, amplitudeAccountName } = selectedBPAccount;
  const { route } = props;
  const { params } = route;
  const { productConfig, productApiData, source } = params;
  const { productId, noOfColumns, productName } = productConfig;

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(
    productConfig.hasAccountInput ? null : mobileNumber,
  );

  useEffect(() => {
    setBPPhone(bpPhone);
    if (!productConfig.hasAccountInput) {
      setSelectedAccountNumber(bpPhone.mobileNumber);
    }
  }, [bpPhone]);

  const { t } = useTranslation('common');
  const toast = useToast();
  const navigation = useNavigation();

  useEffect(() => {
    navigation.setOptions({ title: productName });
  }, [productName]);

  const isFavourite = useFavourites(
    navigation,
    productApiData?.id,
    productApiData?.isDefaultFavorites || productApiData?.isFavorites,
    source,
    productApiData?.prefix,
    productApiData?.canAddFavorites,
  );

  const [nominalsData, setNominalsData] = useState<any[]>([]);
  const [openRTS, setOpenRTS] = useState(false);
  const [nominalSelected, setNominalSelected] = useState<{ id: number }>({ id: -1 });
  const [bpReqData, setBpReqData] = useState<any>({});
  const [showEmptyView, setShowEmptyView] = useState(false);
  const [sAccountInputError, setAccountInputError] = useState('');

  useEffect(() => {
    if (selectedAccountNumber && selectedAccountNumber.length > 0) {
      setAccountInputError('');
    }
  }, [selectedAccountNumber]);

  const { isLoading, isSuccess, data, isError, error, isStale, refetch } = fetchSubProductsOrNominals({
    billAccountNumber: mobileNumber,
    productId,
    accountTypeId,
  });

  const { requestBP, isBPReqError, bpError, apiPayload } = useBPMutation({
    setOpenRTS,
    setBpReqData,
  });

  useEffect(() => {
    refetch();
  }, [bpPhone]);

  useEffect(() => {
    logPageLaunch(productConfig, source, validatedReferralCode, isFavourite);
    refetchBPIfStale(isStale, refetch);
    const backAction = () => {
      logPageBack(productConfig, source, validatedReferralCode);
    };
    return () => {
      backAction();
    };
  }, []);

  useEffect(() => {
    logErrorOnAnalytics(
      isBPReqError,
      selectedAccountNumber || '',
      productConfig,
      apiPayload,
      t,
      bpError,
      source,
      validatedReferralCode,
      isFavourite,
    );
  }, [isBPReqError, bpError]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess && data) {
      const nominalsApiData = data;
      const keys = Object.keys(data);
      if (keys.length > 0) {
        keys.forEach(key => {
          setNominalsData(nominalsApiData[key]);
        });
        setShowEmptyView(false);
      } else {
        setShowEmptyView(true);
      }
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (openRTS && bpReqData) {
      navigation.navigate(navigationConstants.GG_PAYMENT_SUMMARY, {
        bpReqData,
        productConfig,
        selectedBPAccountForConfirm: selectedBPAccount,
        source,
        productApiData,
        selectedAccountNumber,
        nominalSelected,
      });
    }
  }, [openRTS, bpReqData]);

  return (
    <View style={appStyles.safeAreaView}>
      {shouldShowPhoneSelection(params)}
      {shouldShowAccountInput({
        productConfig,
        productApiData,
        setSelectedAccountNumber,
        selectedAccountNumber,
        sAccountInputError,
        setAccountInputError,
        source,
      })}
      {shouldShowTitle(productConfig)}
      {showEmptyView ? (
        <BPNoDataFound />
      ) : (
        showListOfNominals({
          noOfColumns,
          productConfig,
          nominalsData,
          productApiData,
          setOpenRTS,
          setNominalSelected,
          selectedAccountNumber,
          selectedBPAccount,
          requestBP,
          validatedReferralCode,
          t,
          setAccountInputError,
          isLoading,
          toast,
          source,
          meAmplitudeContext,
          amplitudeAccountName,
          isFavourite,
        })
      )}
      {/* <RequestTransactionSheet
        openRTS={openRTS}
        setOpenRTS={setOpenRTS}
        productConfig={productConfig}
        productApiData={productApiData}
        selectedAccountNumber={selectedAccountNumber}
        nominalSelected={nominalSelected}
        bpReqData={bpReqData}
        source={source}
      /> */}
    </View>
  );
}

export default GGNominals;
